import { dataApi } from 'config/axios';

class Reports {
    static async studentEngagement(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/student-engagement${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    static async studentCheckinContext(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/student-checkin-context${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    static async studentCheckinContextDetail(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/student-checkin-context-detail${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    static async checkinContextCount(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/checkin-context-count${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    static async stateOfEmotionByWeek(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/state-of-emotion-by-week${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    /** Per-user check-in counts for readiness trend drill-down (userIds; resolve names client-side). */
    static async readinessTrendExplore(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/readiness-trend-explore${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    /**
     * Readiness trend explore: normalized check-in rows for weekly report v2 drill-down modals.
     * Types: `types/weeklyreportsv2/readinessTrendExplore`.
     */
    static async fetchReadinessTrendExploreCheckins(params = {}) {
        const query = {
            level: params.level,
            levelId: params.levelId,
        };
        if (params.emotionKey) query.emotionKey = params.emotionKey;
        if (params.periodLabel) query.periodLabel = params.periodLabel;
        if (params.gradeId !== undefined && params.gradeId !== '') query.gradeId = params.gradeId;
        if (params.dateRangeMode === 'custom' && params.startDate && params.endDate) {
            query.startDate = params.startDate;
            query.endDate = params.endDate;
        } else {
            query.period = params.period;
            query.interval = params.interval;
            query.term = params.term;
            query.year = params.year;
        }

        const res = await Reports.readinessTrendExplore(query);
        const payload = res?.data ?? res;
        const checkIns = payload?.checkIns ?? [];
        return checkIns.map((c) => ({
            recordId: String(c.recordId ?? ''),
            userId: String(c.userId),
            classId: c.classId != null && c.classId !== '' ? String(c.classId) : '',
            gradeId: c.gradeId != null && c.gradeId !== '' ? String(c.gradeId) : '',
            emotionKey: c.emotionKey != null && c.emotionKey !== '' ? String(c.emotionKey) : '',
            emotionName: String(c.emotionName ?? ''),
            studentContext: String(c.studentContext ?? ''),
            checkInAt: String(c.checkInAt ?? ''),
            teacherComments: Array.isArray(c.teacherComments)
                ? c.teacherComments.map((t) => ({
                      comment: String(t.comment ?? ''),
                      commentedAt: String(t.commentedAt ?? ''),
                  }))
                : [],
        }));
    }

    static async stateOfEmotionByDayOfWeek(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/state-of-emotion-by-day-of-week${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    static async checkInEmotions(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/check-in-emotions${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    static async engagementByClass(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/engagement-by-class${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    static async readyToLearnByClass(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/ready-to-learn-by-class${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    /** Resolves canonical report window dates (matches lsgo-data `resolveDateRange`). */
    static async reportDateRange(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        return await dataApi
            .get(`/api/v2/reports/report-date-range${query ? `?${query}` : ''}`)
            .then((res) => res.data);
    }

    /**
     * Aggregated Weekly Report v2 AI summary (Fae): card summaries + filters + perCardSummaries context.
     * POST body matches `dynamicDashboard/utils/aiSummaryApi.generateReportSummary` (same route and response shape).
     */
    static async weeklyReportAiSummary({ context = {}, paramInfo }) {
        const response = await dataApi.post('/api/v2/reports/ai-summary/report-summary', { context, paramInfo });
        const data = response.data;
        const summaryData = data?.summary || data?.data?.summary || data;
        const summaryText = summaryData?.text || summaryData?.summary || '';
        const widget_info = data?.widget_info || data?.data?.widget_info || data?.data?.data?.widget_info;
        return {
            text: summaryText || 'No summary available',
            summary: summaryText || 'No Summary available',
            timestamp: summaryData?.timestamp || new Date().toISOString(),
            generatedAt: summaryData?.generatedAt || new Date().toISOString(),
            references: summaryData?.references || [],
            widget_info,
        };
    }

    /**
     * Enqueue combined card + report AI generation onto SQS.
     * Lambda consumes the queue and calls POST /ai-summary/generate.
     * Returns { data: { schoolId, reportId, messageId } }.
     */
    static async enqueueCombinedReportAiSummary({ schoolId, templateId, reportId }) {
        const resolvedReportId = reportId ?? templateId;
        const response = await dataApi.post('/api/v2/reports/ai-summary/queue-ai-summary-request', {
            schoolId,
            reportId: resolvedReportId,
            templateId: templateId ?? reportId,
        });
        return response.data;
    }

    /** Poll combined report AI generation job status. */
    static async getCombinedReportAiStatus(jobId) {
        const response = await dataApi.get(`/api/v2/reports/ai-summary/generate/${jobId}`);
        return response.data;
    }

    /** Completed/failed generate jobs awaiting UI acknowledgement. */
    static async getPendingCombinedReportAi() {
        const response = await dataApi.get('/api/v2/reports/ai-summary/generate/pending');
        return response.data;
    }

    /** Acknowledge a completed/failed generate job after the UI has surfaced it. */
    static async ackCombinedReportAi(jobId) {
        const response = await dataApi.post(`/api/v2/reports/ai-summary/generate/${jobId}/ack`);
        return response.data;
    }

    // --- Weekly report v2: per-card Fae summaries (placeholder; replace body with dataApi.post/get when endpoints exist) ---

    static async aiSummaryStudentEngagementCard({ params = {}, widgetInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/student-engagement', { params, widgetInfo })
            .then((res) => res.data);
        return Promise.resolve({
            success: true,
            data: {
                text: res?.aiSummary?.summary,
                summary: res?.aiSummary?.summary,
                widget_info: res?.aiSummary?.widget_info,
            },
        });
    }

    static async aiSummaryReadyToLearnCard({ params = {}, widgetInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/ready-to-learn', { params, widgetInfo })
            .then((res) => res.data);
        return Promise.resolve({
            success: true,
            data: {
                text: res?.aiSummary?.summary,
                summary: res?.aiSummary?.summary,
                widget_info: res?.aiSummary?.widget_info,
            },
        });
    }

    static async aiSummaryStateOfEmotionCard({ params = {}, widgetInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/state-of-emotion', { params, widgetInfo })
            .then((res) => res.data);
        return Promise.resolve({
            success: true,
            data: {
                text: res?.aiSummary?.summary,
                summary: res?.aiSummary?.summary,
                widget_info: res?.aiSummary?.widget_info,
            },
        });
    }

    static async aiSummaryStateOfEmotionByDayOfWeekCard({ params = {}, widgetInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/state-of-emotion-by-day-of-week', { params, widgetInfo })
            .then((res) => res.data);
        return Promise.resolve({
            success: true,
            data: {
                text: res?.aiSummary?.summary,
                summary: res?.aiSummary?.summary,
                widget_info: res?.aiSummary?.widget_info,
            },
        });
    }

    static async aiSummaryReadinessTrendCard({ params = {}, widgetInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/state-of-emotion-by-week', { params, widgetInfo })
            .then((res) => res.data);
        return Promise.resolve({
            success: true,
            data: {
                text: res?.aiSummary?.summary,
                summary: res?.aiSummary?.summary,
                widget_info: res?.aiSummary?.widget_info,
            },
        });
    }

    static async aiSummaryContextComparisonCard({ params = {}, widgetInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/context-comparison', { params, widgetInfo })
            .then((res) => res.data);
        return Promise.resolve({
            success: true,
            data: {
                text: res?.aiSummary?.summary,
                summary: res?.aiSummary?.summary,
                widget_info: res?.aiSummary?.widget_info,
            },
        });
    }

    static async aiSummaryCheckInEmotionsCard({ params = {}, widgetInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/check-in-emotions', { params, widgetInfo })
            .then((res) => res.data);
        return Promise.resolve({
            success: true,
            data: {
                text: res?.aiSummary?.summary,
                summary: res?.aiSummary?.summary,
                widget_info: res?.aiSummary?.widget_info,
            },
        });
    }

    static async aiSummaryClassEngagementCard({ params = {}, widgetInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/class-engagement', { params, widgetInfo })
            .then((res) => res.data);
        return Promise.resolve({
            success: true,
            data: {
                text: res?.aiSummary?.summary,
                summary: res?.aiSummary?.summary,
                widget_info: res?.aiSummary?.widget_info,
            },
        });
    }

    static async setSummaryRating({ widgetInfo, feedbackInfo }) {
        const res = await dataApi
            .post('/api/v2/reports/ai-summary/summary-rating', { widgetInfo, feedbackInfo })
            .then((res) => res.data);
        console.log(res);
    }
}

export default Reports;
